/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Unauthorized copying, scraping, redistribution, or commercial reuse is prohibited.
 */

import { Link } from "react-router-dom";
import PolicyLanguageToggle, {
  usePolicyLanguage,
} from "../components/PolicyLanguageToggle";

const copy = {
  es: {
    title: "Política de privacidad",
    date: "Vigente desde el 30 de julio de 2026 · Versión 2026-07-30",
    introduction:
      "Esta Política explica qué información recopila BioIndex™, por qué la utiliza, cuándo se vuelve pública, qué proveedores la procesan y qué opciones tienen los usuarios.",
    returnHome: "Volver a BioIndex",
    rights: "Todos los derechos reservados.",
    sections: [
      {
        title: "Quién opera BioIndex",
        text: "BioIndex™ es operado por Luis Carlos Beltran en Colombia. Esta Política se aplica al sitio web, la aplicación móvil, las funciones comunitarias y los servicios relacionados de BioIndex. BioIndex determina por qué y cómo se utiliza la información enviada a la plataforma, mientras que los proveedores de servicios procesan información en nombre de BioIndex.",
      },
      {
        title: "Información de la cuenta y el perfil",
        text: "Cuando inicias sesión, BioIndex recibe y almacena información como tu identificador de usuario de Firebase, la dirección de correo electrónico de tu cuenta de Google, tu nombre visible y tu fotografía de perfil. BioIndex también puede almacenar información que agregues al perfil, como una biografía, fotografías destacadas, títulos, colecciones, amigos, preferencias y ajustes del perfil.",
      },
      {
        title: "Observaciones y contenido cargado",
        text: "BioIndex almacena las fotografías, nombres taxonómicos, nombres comunes, fechas de observación, ubicaciones, coordenadas, notas, campos adicionales, información de colecciones y otros detalles que decidas enviar. Los archivos originales pueden contener metadatos proporcionados por tu dispositivo. Debes eliminar antes de cargar cualquier información que no quieras compartir.",
      },
      {
        title: "Información comunitaria y de actividad",
        text: "BioIndex puede almacenar temas de foros, comentarios, respuestas, reacciones, votos, kudos, conexiones de amistad, notificaciones, denuncias, bloqueos, registros de moderación, cuestionarios, retos, títulos desbloqueados, progreso taxonómico, actividad reciente y otras interacciones asociadas con tu cuenta.",
      },
      {
        title: "Información de ubicación",
        text: "BioIndex accede a la ubicación de tu dispositivo únicamente cuando utilizas activamente una función de ubicación, como “Usar mi ubicación”. BioIndex no requiere acceso continuo a la ubicación en segundo plano. Puedes rechazar el permiso e ingresar o seleccionar una ubicación manualmente. Si guardas una ubicación precisa con una observación, esta pasa a formar parte del registro y puede ser visible para otros usuarios.",
      },
      {
        title: "Fotografías y permisos del dispositivo",
        text: "BioIndex solicita acceso a las fotografías o la cámara únicamente cuando decides seleccionar, capturar o cargar una imagen. Las opciones de permisos se controlan desde los ajustes de tu dispositivo. Rechazar un permiso opcional puede desactivar la función correspondiente, pero no debería impedir el acceso a otras partes de BioIndex.",
      },
      {
        title: "Información técnica",
        text: "BioIndex y sus proveedores pueden procesar información técnica necesaria para operar y proteger la plataforma, como la dirección IP, el tipo de navegador o dispositivo, sistema operativo, versión de la aplicación, eventos de autenticación, marcas de tiempo, información de errores, registros de seguridad, cookies, almacenamiento local e identificadores similares.",
      },
      {
        title: "Cómo se utiliza la información",
        text: "La información se utiliza para autenticar usuarios, mantener cuentas, almacenar y mostrar observaciones, organizar registros de biodiversidad, proporcionar perfiles y funciones sociales, operar cuestionarios y colecciones, responder solicitudes, investigar denuncias, prevenir abusos, proteger la plataforma, diagnosticar errores, aplicar las políticas de BioIndex, cumplir obligaciones legales y mejorar las funciones existentes.",
      },
      {
        title: "Información pública",
        text: "Tu nombre visible, fotografía de perfil, información del perfil, observaciones, fotografías cargadas, identificaciones taxonómicas, fechas, ubicaciones guardadas, notas, colecciones, títulos, contribuciones en foros, comentarios, reacciones y actividad comunitaria similar pueden ser visibles para otros usuarios. No publiques direcciones privadas, información confidencial ni ubicaciones exactas que puedan poner en riesgo a personas, áreas protegidas o especies vulnerables.",
      },
      {
        title: "Proveedores y procesamiento internacional",
        text: "BioIndex utiliza servicios proporcionados por Google, entre ellos Google Sign-In, Firebase Authentication, Cloud Firestore, Cloud Storage, Firebase Hosting y Google Maps Platform cuando corresponda. Estos servicios pueden procesar información en infraestructura ubicada fuera de Colombia o del país donde vives. BioIndex puede utilizar proveedores adicionales únicamente cuando sean necesarios para operar una función claramente identificada.",
      },
      {
        title: "Intercambio de información",
        text: (
          <>
            BioIndex <strong>no vende información personal</strong>. La
            información puede compartirse con proveedores que operan la
            plataforma, con otros usuarios cuando publicas contenido, cuando lo
            exige la ley o un proceso legal válido, para investigar abusos o
            incidentes de seguridad, para proteger a usuarios o a la vida
            silvestre, o como parte de una futura transferencia de la plataforma
            con las salvaguardas y notificaciones correspondientes.
          </>
        ),
      },
      {
        title: "Conservación y eliminación de cuentas",
        text: (
          <>
            La información generalmente se conserva mientras tu cuenta
            permanezca activa o mientras sea necesaria para proporcionar
            BioIndex. Puedes solicitar la eliminación de tu cuenta mediante los
            controles disponibles en tu Perfil. Si no puedes acceder a tu
            cuenta, envía una solicitud mediante la{" "}
            <Link
              to="/contact"
              className="font-medium text-[#01767A] underline"
            >
              página de contacto de BioIndex
            </Link>
            . Después de una solicitud válida, la información personal se
            eliminará o desidentificará dentro de un plazo razonable, salvo
            cuando sea necesario conservarla por seguridad, prevención del
            fraude, cumplimiento legal, resolución de disputas, moderación o
            aplicación de políticas. La eliminación de respaldos y cachés puede
            requerir tiempo adicional.
          </>
        ),
      },
      {
        title: "Tus opciones y derechos",
        text: "Puedes revisar y actualizar la información disponible de tu perfil y tus observaciones mediante BioIndex. Dependiendo de la ley aplicable, también puedes solicitar acceso, corrección, eliminación, restricción o una copia de tu información personal, y retirar tu consentimiento cuando el tratamiento se base en él. Puedes controlar los permisos del dispositivo desde los ajustes de tu sistema operativo. Retirar un permiso necesario o solicitar la eliminación puede hacer que algunas funciones dejen de estar disponibles.",
      },
      {
        title: "Niños y adolescentes",
        text: "BioIndex no está destinado a menores de 13 años y no recopila deliberadamente su información personal. Los usuarios que legalmente no puedan autorizar el tratamiento de su propia información solo deben utilizar BioIndex con el permiso y la supervisión exigidos por las leyes del lugar donde viven. Si BioIndex descubre que recopiló información de un menor sin la autorización requerida, puede eliminar la cuenta y la información asociada.",
      },
      {
        title: "Seguridad",
        text: "BioIndex utiliza controles de acceso, servicios autenticados, reglas de seguridad e infraestructura confiable en la nube destinados a proteger las cuentas y los datos de la plataforma. Ningún servicio en línea puede garantizar una seguridad absoluta. Los usuarios deben proteger el acceso a su cuenta de Google e informar prontamente cualquier actividad no autorizada que sospechen.",
      },
      {
        title: "Cambios en esta Política",
        text: "BioIndex puede modificar esta Política de privacidad a medida que evolucionen sus funciones y prácticas. La fecha de entrada en vigor y la versión actuales permanecerán visibles en esta página. Los cambios sustanciales pueden presentarse dentro de BioIndex y requerir una nueva confirmación o consentimiento cuando corresponda.",
      },
      {
        title: "Contacto",
        text: (
          <>
            Las preguntas y solicitudes sobre privacidad pueden enviarse
            mediante la{" "}
            <Link
              to="/contact"
              className="font-medium text-[#01767A] underline"
            >
              página de contacto de BioIndex
            </Link>
            . Incluye la dirección de correo electrónico asociada con tu cuenta
            de BioIndex para que pueda verificarse la solicitud.
          </>
        ),
      },
    ],
  },
  en: {
    title: "Privacy Policy",
    date: "Effective July 30, 2026 · Version 2026-07-30",
    introduction:
      "This Privacy Policy explains what information BioIndex™ collects, why it is used, when it becomes public, which providers process it, and the choices available to users.",
    returnHome: "Return to BioIndex",
    rights: "All rights reserved.",
    sections: [
      {
        title: "Who operates BioIndex",
        text: "BioIndex™ is operated by Luis Carlos Beltran in Colombia. This Policy applies to the BioIndex website, mobile application, community features, and related services. BioIndex determines why and how information submitted to the platform is used, while service providers process information on BioIndex's behalf.",
      },
      {
        title: "Account and profile information",
        text: "When you sign in, BioIndex receives and stores information such as your Firebase user identifier, Google account email address, display name, and profile photograph. BioIndex may also store information you add to your profile, including a biography, featured photographs, titles, collections, friends, preferences, and profile settings.",
      },
      {
        title: "Observations and uploaded content",
        text: "BioIndex stores the photographs, taxonomic names, common names, observation dates, locations, coordinates, notes, additional fields, collection information, and other details you choose to submit. Original files may contain metadata supplied by your device. You should remove information you do not want to share before uploading.",
      },
      {
        title: "Community and activity information",
        text: "BioIndex may store forum topics, comments, replies, reactions, votes, kudos, friend connections, notifications, reports, blocks, moderation records, quizzes, challenges, unlocked titles, taxonomic progress, recent activity, and other interactions associated with your account.",
      },
      {
        title: "Location information",
        text: "BioIndex accesses your device location only when you actively use a location feature, such as “Use my location.” BioIndex does not require continuous background location access. You may decline the permission and enter or select a location manually. If you save a precise location with an observation, that location becomes part of the observation record and may be visible to other users.",
      },
      {
        title: "Photographs and device permissions",
        text: "BioIndex requests access to photographs or the camera only when you choose to select, capture, or upload an image. Permission choices are controlled through your device settings. Declining an optional permission may disable the related feature but should not prevent access to unrelated parts of BioIndex.",
      },
      {
        title: "Technical information",
        text: "BioIndex and its service providers may process technical information needed to operate and secure the platform, including IP address, browser or device type, operating system, app version, authentication events, timestamps, error information, security logs, cookies, local storage, and similar identifiers.",
      },
      {
        title: "How information is used",
        text: "Information is used to authenticate users, maintain accounts, store and display observations, organize biodiversity records, provide profiles and social features, operate quizzes and collections, respond to requests, investigate reports, prevent abuse, protect the platform, diagnose errors, enforce BioIndex policies, comply with legal obligations, and improve existing features.",
      },
      {
        title: "Public information",
        text: "Your display name, profile photograph, profile information, observations, uploaded photographs, taxonomic identifications, dates, saved locations, notes, collections, titles, forum contributions, comments, reactions, and similar community activity may be visible to other users. Do not publish private addresses, confidential information, or exact locations that could endanger people, protected areas, or vulnerable species.",
      },
      {
        title: "Service providers and international processing",
        text: "BioIndex uses services provided by Google, including Google Sign-In, Firebase Authentication, Cloud Firestore, Cloud Storage, Firebase Hosting, and Google Maps Platform where applicable. These services may process information on infrastructure located outside Colombia or outside the country where you live. BioIndex may use additional providers only when needed to operate a clearly identified feature.",
      },
      {
        title: "Sharing of information",
        text: (
          <>
            BioIndex <strong>does not sell personal information</strong>.
            Information may be shared with service providers that operate the
            platform, with other users when you publish content, when required by
            law or a valid legal process, to investigate abuse or security
            incidents, to protect users or wildlife, or as part of a future
            transfer of the platform with appropriate safeguards and notice.
          </>
        ),
      },
      {
        title: "Retention and account deletion",
        text: (
          <>
            Information is generally retained while your account remains active
            or while it is needed to provide BioIndex. You may request account
            deletion through the account controls in your Profile. If you cannot
            access your account, submit a request through the{" "}
            <Link
              to="/contact"
              className="font-medium text-[#01767A] underline"
            >
              BioIndex Contact Page
            </Link>
            . Following a valid request, personal information will be deleted or
            de-identified within a reasonable period, except where retention is
            necessary for security, fraud prevention, legal compliance, dispute
            resolution, moderation, or enforcement. Removal from backups and
            caches may take additional time.
          </>
        ),
      },
      {
        title: "Your choices and rights",
        text: "You may review and update available profile and observation information through BioIndex. Depending on applicable law, you may also request access, correction, deletion, restriction, or a copy of personal information and may withdraw consent where processing is based on consent. You may control device permissions through your operating-system settings. Withdrawing a required permission or requesting deletion may make some features unavailable.",
      },
      {
        title: "Children and adolescents",
        text: "BioIndex is not intended for children under 13 and does not knowingly collect their personal information. Users who are not legally able to authorize the processing of their own information must use BioIndex only with the permission and supervision required by the laws where they live. If BioIndex learns that information was collected from a child without the required authorization, it may remove the account and associated information.",
      },
      {
        title: "Security",
        text: "BioIndex uses access controls, authenticated services, security rules, and trusted cloud infrastructure intended to protect accounts and platform data. No online service can guarantee absolute security. Users should protect access to their Google account and report suspected unauthorized activity promptly.",
      },
      {
        title: "Changes to this Policy",
        text: "BioIndex may revise this Privacy Policy as its features and practices evolve. The current effective date and version will remain displayed on this page. Material changes may be presented inside BioIndex and may require renewed acknowledgement or consent when appropriate.",
      },
      {
        title: "Contact",
        text: (
          <>
            Privacy questions and requests may be submitted through the{" "}
            <Link
              to="/contact"
              className="font-medium text-[#01767A] underline"
            >
              BioIndex Contact Page
            </Link>
            . Include the email address associated with your BioIndex account so
            the request can be verified.
          </>
        ),
      },
    ],
  },
};

export default function PrivacyPage() {
  const [language, setLanguage] = usePolicyLanguage();
  const content = copy[language];

  return (
    <main className="min-h-screen bg-[#374049] px-4 py-8 text-[#374049]">
      <section className="mx-auto max-w-5xl overflow-hidden rounded-3xl border border-[#93CA87]/40 bg-[#FCFFF6] shadow-lg">
        <div className="flex items-center justify-center bg-[#374049] px-6 py-6">
          <img
            src="/BioIndex Prototype Banner.png"
            alt="BioIndex"
            className="max-h-32 w-full max-w-md object-contain"
          />
        </div>

        <div className="px-6 py-8 sm:px-10 lg:px-14">
          <div className="flex justify-end">
            <PolicyLanguageToggle
              language={language}
              onChange={setLanguage}
            />
          </div>

          <div className="mt-6 max-w-3xl border-b border-[#374049]/10 pb-6">
            <h1
              className="text-4xl font-semibold"
              style={{ fontFamily: "Cormorant Garamond" }}
            >
              {content.title}
            </h1>

            <p className="mt-2 text-xs text-[#374049]/50">{content.date}</p>

            <p className="mt-5 text-sm leading-7 text-[#374049]/75">
              {content.introduction}
            </p>
          </div>

          <div className="mt-7 grid gap-4 md:grid-cols-2">
            {content.sections.map((section, index) => (
              <article
                key={section.title}
                className="rounded-2xl border border-[#374049]/10 bg-white/70 p-5 shadow-sm"
              >
                <div className="mb-3 flex items-center gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[#24C3BE]/10 text-sm font-semibold text-[#24C3BE]">
                    {index + 1}
                  </span>
                  <h2 className="text-base font-semibold text-[#374049]">
                    {section.title}
                  </h2>
                </div>

                <div className="text-sm leading-7 text-[#374049]/70">
                  {section.text}
                </div>
              </article>
            ))}
          </div>

          <div className="mt-8 flex flex-col items-center border-t border-[#374049]/10 pt-7 text-center">
            <Link
              to="/"
              className="inline-flex items-center justify-center rounded-xl bg-[#374049] px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-[#2f373e]"
            >
              {content.returnHome}
            </Link>

            <p className="mt-5 text-xs leading-6 text-[#374049]/55">
              © {new Date().getFullYear()} BioIndex™. {content.rights}
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}
